// src/shared/api-client/endpoints/officials.js
// Political Intelligence CRM — Officials API endpoints

import { baseApi } from "../baseApi";

const officialsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // -------------------------------------------------------------------------
    // JURISDICTIONS
    // -------------------------------------------------------------------------

    getJurisdictions: builder.query({
      query: () => "/api/federation/jurisdictions",
      providesTags: (result) =>
        result?.data?.jurisdictions
          ? [
              ...result.data.jurisdictions.map(({ id }) => ({
                type: "PoliticalJurisdiction",
                id,
              })),
              { type: "PoliticalJurisdiction", id: "LIST" },
            ]
          : [{ type: "PoliticalJurisdiction", id: "LIST" }],
    }),

    createJurisdiction: builder.mutation({
      query: (data) => ({
        url: "/api/federation/jurisdictions",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "PoliticalJurisdiction", id: "LIST" }],
    }),

    updateJurisdiction: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/federation/jurisdictions/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PoliticalJurisdiction", id },
        { type: "PoliticalJurisdiction", id: "LIST" },
        { type: "PoliticalOfficial", id: "LIST" },
      ],
    }),

    deleteJurisdiction: builder.mutation({
      query: (id) => ({
        url: `/api/federation/jurisdictions/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "PoliticalJurisdiction", id: "LIST" },
        { type: "PoliticalOfficial", id: "LIST" },
      ],
    }),

    // -------------------------------------------------------------------------
    // OFFICIALS
    // -------------------------------------------------------------------------

    getOfficials: builder.query({
      query: (params = {}) => ({ url: "/api/federation/officials", params }),
      providesTags: (result) => {
        const officials = Array.isArray(result?.data) ? result.data : [];
        return [
          ...officials.map(({ id }) => ({ type: "PoliticalOfficial", id })),
          { type: "PoliticalOfficial", id: "LIST" },
        ];
      },
    }),

    getOfficialById: builder.query({
      query: (id) => `/api/federation/officials/${id}`,
      providesTags: (_result, _error, id) => [
        { type: "PoliticalOfficial", id },
      ],
    }),

    createOfficial: builder.mutation({
      query: (data) => ({
        url: "/api/federation/officials",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "PoliticalOfficial", id: "LIST" }],
    }),

    updateOfficial: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/federation/officials/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "PoliticalOfficial", id },
        { type: "PoliticalOfficial", id: "LIST" },
      ],
    }),

    deleteOfficial: builder.mutation({
      query: (id) => ({
        url: `/api/federation/officials/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: "PoliticalOfficial", id },
        { type: "PoliticalOfficial", id: "LIST" },
      ],
    }),

    // -------------------------------------------------------------------------
    // STAFFERS
    // -------------------------------------------------------------------------

    getOfficialStaffers: builder.query({
      query: (officialId) => `/api/officials/${officialId}/staffers`,
      providesTags: (_result, _error, officialId) => [
        { type: "PoliticalStaffer", id: `OFFICIAL_${officialId}` },
      ],
    }),

    createStaffer: builder.mutation({
      query: ({ officialId, ...data }) => ({
        url: `/api/officials/${officialId}/staffers`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalStaffer", id: `OFFICIAL_${officialId}` },
      ],
    }),

    updateStaffer: builder.mutation({
      query: ({ officialId, stafferId, ...data }) => ({
        url: `/api/officials/${officialId}/staffers/${stafferId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalStaffer", id: `OFFICIAL_${officialId}` },
      ],
    }),

    deleteStaffer: builder.mutation({
      query: ({ officialId, stafferId }) => ({
        url: `/api/officials/${officialId}/staffers/${stafferId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalStaffer", id: `OFFICIAL_${officialId}` },
      ],
    }),

    // -------------------------------------------------------------------------
    // STANCES
    // -------------------------------------------------------------------------

    getStances: builder.query({
      query: (officialId) => `/api/officials/${officialId}/stances`,
      providesTags: (_result, _error, officialId) => [
        { type: "OfficialStance", id: `OFFICIAL_${officialId}` },
      ],
    }),

    upsertStance: builder.mutation({
      query: ({ officialId, ...data }) => ({
        url: `/api/officials/${officialId}/stances`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "OfficialStance", id: `OFFICIAL_${officialId}` },
      ],
    }),

    getStanceHistory: builder.query({
      query: ({ officialId, stanceId }) =>
        `/api/officials/${officialId}/stances/${stanceId}/history`,
      providesTags: (_result, _error, { stanceId }) => [
        { type: "OfficialStance", id: `HISTORY_${stanceId}` },
      ],
    }),

    // -------------------------------------------------------------------------
    // INTERACTIONS
    // -------------------------------------------------------------------------

    getInteractions: builder.query({
      query: (officialId) => `/api/officials/${officialId}/interactions`,
      providesTags: (_result, _error, officialId) => [
        { type: "PoliticalInteraction", id: `OFFICIAL_${officialId}` },
      ],
    }),

    createInteraction: builder.mutation({
      query: ({ officialId, ...data }) => ({
        url: `/api/officials/${officialId}/interactions`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalInteraction", id: `OFFICIAL_${officialId}` },
      ],
    }),

    updateInteraction: builder.mutation({
      query: ({ officialId, interactionId, ...data }) => ({
        url: `/api/officials/${officialId}/interactions/${interactionId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalInteraction", id: `OFFICIAL_${officialId}` },
      ],
    }),

    deleteInteraction: builder.mutation({
      query: ({ officialId, interactionId }) => ({
        url: `/api/officials/${officialId}/interactions/${interactionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalInteraction", id: `OFFICIAL_${officialId}` },
      ],
    }),

    // -------------------------------------------------------------------------
    // COMMITTEES
    // -------------------------------------------------------------------------

    getOfficialCommittees: builder.query({
      query: (officialId) => `/api/officials/${officialId}/committees`,
      providesTags: (_result, _error, officialId) => [
        { type: "PoliticalCommittee", id: `OFFICIAL_${officialId}` },
      ],
    }),

    addOfficialToCommittee: builder.mutation({
      query: ({ officialId, ...data }) => ({
        url: `/api/officials/${officialId}/committees`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalCommittee", id: `OFFICIAL_${officialId}` },
        { type: "PoliticalCommittee", id: "LIST" },
      ],
    }),

    removeOfficialFromCommittee: builder.mutation({
      query: ({ officialId, junctionId }) => ({
        url: `/api/officials/${officialId}/committees/${junctionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalCommittee", id: `OFFICIAL_${officialId}` },
        { type: "PoliticalCommittee", id: "LIST" },
      ],
    }),

    // -------------------------------------------------------------------------
    // DOCUMENTS
    // -------------------------------------------------------------------------

    getOfficialDocuments: builder.query({
      query: (officialId) => `/api/officials/${officialId}/documents`,
      providesTags: (_result, _error, officialId) => [
        { type: "PoliticalDocument", id: `OFFICIAL_${officialId}` },
      ],
    }),

    getOfficialDocumentUploadUrl: builder.mutation({
      query: ({ officialId, ...data }) => ({
        url: `/api/officials/${officialId}/documents/upload-url`,
        method: "POST",
        body: data,
      }),
    }),

    createOfficialDocument: builder.mutation({
      query: ({ officialId, ...data }) => ({
        url: `/api/officials/${officialId}/documents`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalDocument", id: `OFFICIAL_${officialId}` },
      ],
    }),

    downloadOfficialDocument: builder.mutation({
      query: ({ officialId, docId }) => ({
        url: `/api/officials/${officialId}/documents/${docId}/download`,
        method: "GET",
      }),
    }),

    deleteOfficialDocument: builder.mutation({
      query: ({ officialId, docId }) => ({
        url: `/api/officials/${officialId}/documents/${docId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { officialId }) => [
        { type: "PoliticalDocument", id: `OFFICIAL_${officialId}` },
      ],
    }),

    // -------------------------------------------------------------------------
    // STAFFER INTERACTIONS (via /api/staffers/:stafferId/interactions)
    // -------------------------------------------------------------------------

    getStafferInteractions: builder.query({
      query: (stafferId) => `/api/staffers/${stafferId}/interactions`,
      providesTags: (_result, _error, stafferId) => [
        { type: "PoliticalInteraction", id: `STAFFER_${stafferId}` },
      ],
    }),

    createStafferInteraction: builder.mutation({
      query: ({ stafferId, ...data }) => ({
        url: `/api/staffers/${stafferId}/interactions`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, { stafferId }) => [
        { type: "PoliticalInteraction", id: `STAFFER_${stafferId}` },
      ],
    }),

    updateStafferInteraction: builder.mutation({
      query: ({ stafferId, interactionId, ...data }) => ({
        url: `/api/staffers/${stafferId}/interactions/${interactionId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { stafferId }) => [
        { type: "PoliticalInteraction", id: `STAFFER_${stafferId}` },
      ],
    }),

    deleteStafferInteraction: builder.mutation({
      query: ({ stafferId, interactionId }) => ({
        url: `/api/staffers/${stafferId}/interactions/${interactionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { stafferId }) => [
        { type: "PoliticalInteraction", id: `STAFFER_${stafferId}` },
      ],
    }),
  }),
});

// Jurisdictions
export const useGetJurisdictionsQuery = officialsApi.useGetJurisdictionsQuery;
export const useCreateJurisdictionMutation =
  officialsApi.useCreateJurisdictionMutation;
export const useUpdateJurisdictionMutation =
  officialsApi.useUpdateJurisdictionMutation;
export const useDeleteJurisdictionMutation =
  officialsApi.useDeleteJurisdictionMutation;

// Officials
export const useGetOfficialsQuery = officialsApi.useGetOfficialsQuery;
export const useGetOfficialByIdQuery = officialsApi.useGetOfficialByIdQuery;
export const useCreateOfficialMutation = officialsApi.useCreateOfficialMutation;
export const useUpdateOfficialMutation = officialsApi.useUpdateOfficialMutation;
export const useDeleteOfficialMutation = officialsApi.useDeleteOfficialMutation;

// Staffers
export const useGetOfficialStaffersQuery =
  officialsApi.useGetOfficialStaffersQuery;
export const useCreateStafferMutation = officialsApi.useCreateStafferMutation;
export const useUpdateStafferMutation = officialsApi.useUpdateStafferMutation;
export const useDeleteStafferMutation = officialsApi.useDeleteStafferMutation;

// Stances
export const useGetStancesQuery = officialsApi.useGetStancesQuery;
export const useUpsertStanceMutation = officialsApi.useUpsertStanceMutation;
export const useGetStanceHistoryQuery = officialsApi.useGetStanceHistoryQuery;

// Interactions
export const useGetInteractionsQuery = officialsApi.useGetInteractionsQuery;
export const useCreateInteractionMutation =
  officialsApi.useCreateInteractionMutation;
export const useUpdateInteractionMutation =
  officialsApi.useUpdateInteractionMutation;
export const useDeleteInteractionMutation =
  officialsApi.useDeleteInteractionMutation;

// Committees (memberships)
export const useGetOfficialCommitteesQuery =
  officialsApi.useGetOfficialCommitteesQuery;
export const useAddOfficialToCommitteeMutation =
  officialsApi.useAddOfficialToCommitteeMutation;
export const useRemoveOfficialFromCommitteeMutation =
  officialsApi.useRemoveOfficialFromCommitteeMutation;

// Documents
export const useGetOfficialDocumentsQuery =
  officialsApi.useGetOfficialDocumentsQuery;
export const useGetOfficialDocumentUploadUrlMutation =
  officialsApi.useGetOfficialDocumentUploadUrlMutation;
export const useCreateOfficialDocumentMutation =
  officialsApi.useCreateOfficialDocumentMutation;
export const useDownloadOfficialDocumentMutation =
  officialsApi.useDownloadOfficialDocumentMutation;
export const useDeleteOfficialDocumentMutation =
  officialsApi.useDeleteOfficialDocumentMutation;

// Staffer interactions
export const useGetStafferInteractionsQuery =
  officialsApi.useGetStafferInteractionsQuery;
export const useCreateStafferInteractionMutation =
  officialsApi.useCreateStafferInteractionMutation;
export const useUpdateStafferInteractionMutation =
  officialsApi.useUpdateStafferInteractionMutation;
export const useDeleteStafferInteractionMutation =
  officialsApi.useDeleteStafferInteractionMutation;
